import { FiBookmark, FiTrash2, FiArrowRight, FiPieChart, FiActivity } from 'react-icons/fi';

export function FavoritesPage({ favoriteItems, userId, onNavigate, onRemoveFavorite, onSectionChange }) {
  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const raw = String(dateString).trim();
    if (!raw) return '';
    const hasTimezone =
      /[zZ]$/.test(raw) ||
      /[+-]\d{2}:?\d{2}$/.test(raw) ||
      /[+-]\d{2}:?\d{2}\s*$/.test(raw);
    const normalized = hasTimezone ? raw : raw.replace(' ', 'T') + 'Z';
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
  };

  return (
    <section className="glass-lg w-full overflow-hidden min-h-[80vh]">
      <div className="p-6 md:p-8 lg:p-10 flex flex-col gap-8">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold tracking-[0.25em] text-slate-500 uppercase mb-2">WATCHLIST</p>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <FiBookmark className="text-sky-400" />
                我的收藏
              </h1>
              <span className="px-2 py-0.5 rounded-md bg-slate-800/50 text-xs font-mono text-slate-400 border border-slate-700/50">
                {favoriteItems.length}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-slate-400 bg-slate-900/40 px-4 py-2 rounded-full border border-slate-800/50">
             <span className="w-2 h-2 rounded-full bg-sky-500 animate-pulse"></span>
             <span>实时更新</span>
          </div>
        </div>

        {favoriteItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 rounded-[2rem] border border-dashed border-slate-800 bg-slate-900/20 text-center">
            <div className="w-20 h-20 mb-6 rounded-full bg-slate-800/50 flex items-center justify-center shadow-inner">
               <FiBookmark className="w-8 h-8 text-slate-600" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">暂无收藏市场</h3>
            <p className="text-slate-400 text-sm max-w-md px-4">
              在 "推荐" 页面浏览感兴趣的市场，点击收藏图标即可添加到这里，方便随时追踪动态。
            </p>
            <button 
              onClick={() => onSectionChange('recommend')}
              className="mt-8 px-6 py-2.5 rounded-full bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border border-sky-500/20 hover:border-sky-500/40 transition-all flex items-center gap-2 text-sm font-medium"
            >
              去浏览市场
              <FiArrowRight />
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
            {favoriteItems.map((it) => (
              <div 
                key={it.slug} 
                className="group relative flex flex-col h-full min-h-[180px] p-5 rounded-[1.5rem] bg-slate-800/30 border border-slate-700/50 hover:bg-slate-800/50 hover:border-slate-600 transition-all duration-300 backdrop-blur-md overflow-hidden"
              >
                {/* Decorative Background Gradient */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-sky-500/5 to-purple-500/5 rounded-bl-[4rem] pointer-events-none" />

                {/* Top Meta */}
                <div className="flex justify-between items-start mb-3 relative z-10">
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-1 rounded-lg bg-slate-900/50 text-[0.65rem] font-bold text-slate-400 border border-slate-800 uppercase tracking-wider">
                      {(it.categories && it.categories[0]?.label) || '综合'}
                    </span>
                    {it.volume && (
                      <span className="flex items-center gap-1 text-[0.65rem] text-slate-500">
                        <FiActivity className="w-3 h-3" />
                        ${(it.volume / 1000).toFixed(1)}k Vol
                      </span>
                    )}
                  </div>
                  
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveFavorite(it.slug);
                    }}
                    className="p-2 rounded-full text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                    title="取消收藏"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Title */}
                <h3 
                  onClick={() => onNavigate(`/favorites/${it.slug}`)}
                  className="text-base md:text-lg font-bold text-white leading-snug line-clamp-3 mb-4 flex-grow cursor-pointer group-hover:text-sky-300 transition-colors relative z-10"
                >
                  {(it.i18n && it.i18n.zh_CN) || it.question || it.slug}
                </h3>

                {/* Bottom Actions & Info */}
                <div className="mt-auto pt-4 border-t border-slate-700/30 flex items-center justify-between relative z-10">
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    {it.end_date && (
                      <span title="结束日期">截止: {formatDate(it.end_date)}</span>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => onNavigate(`/favorites/${it.slug}`)}
                    className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-700/30 hover:bg-sky-500 hover:text-white text-slate-300 text-xs font-medium transition-all border border-slate-600/30 hover:border-sky-400 shadow-sm group-hover:shadow-md"
                  >
                    <FiPieChart className="w-3.5 h-3.5" />
                    查看详情
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
